<template>
	<div class="modal-breakdown modal-wrapper">
		<ValidationObserver v-slot="{ passes, invalid }" tag="form" class="modal-breakdown-container" @submit.prevent>
			<div class="h-full">
				<div>
					<div class="event-sidebar-btn-back font-uppercase">
						{{ $tc("modals.highlightCreating.title") }}
					</div>
				</div>
				<div class="flex flex-col justify-between overflow-auto" style="height: calc(100% - 50px);">
					<div class="p-20 pb-100 mt-25 overflow-x-hidden">
						<div>
							<TextInput
								v-model="clipData.name"
								:label="$t('modals.highlightCreating.nameYourClipText')"
								name="clip name"
								type="text"
								class="clip-name small-text"
								rules="required|min:3|max:50"
							/>
						</div>
						<div class="small-text text-white font-uppercase mb-20 mt-50" style="opacity: 0.7">
							{{ $tc("modals.highlightCreating.selectAudioOptionsText") }}
						</div>
						<div class="flex justify-between mb-10">
							<Checkbox
								class="highlight-checkbox small-text text-white font-uppercase"
								:value="isAudioSelected"
								:label="$t('modals.highlightCreating.keepRecordedAudioText')"
								@input="onAudioCheckboxChange"
							/>
							<SVGIcon class="mr-5" height="30" icon-name="audio" width="30" />
						</div>
						<div class="flex justify-between mb-10">
							<Checkbox
								class="highlight-checkbox small-text text-white font-uppercase"
								:value="isMusicSelected"
								:label="$t('modals.highlightCreating.addMusicText')"
								@input="onMusicCheckboxChange"
							/>
							<SVGIcon class="mr-5" height="30" icon-name="music" width="30" />
						</div>
						<transition mode="out-in" name="swipe-left">
							<SoundSelector v-if="isMusicSelected" v-model="clipData.soundtrack" />
						</transition>
					</div>
					<div class="tags-sections-actions">
						<div class="small-text text-white text-center text-secondary mb-20">
							{{ $tc("modals.highlightCreating.infoClipCreatingText") }}
						</div>
						<transition name="popup-slide-small">
							<div class="bulk-buttons form-group font-uppercase text-truncate">
								<button
									class="button-cancel font-regular"
									title="Cancel"
									@click="onClose"
								>
									{{ $tc("modals.highlightCreating.cancelButtonText") }}
								</button>
								<button
									v-loading="loading"
									:disabled="loading || invalid"
									class="button-light-blue font-regular"
									title="Save"
									@click="passes(onSave)"
								>
									{{ $tc("modals.highlightCreating.createClipButtonText") }}
								</button>
							</div>
						</transition>
					</div>
				</div>
			</div>
		</ValidationObserver>
	</div>
</template>

<script>
import { mapActions } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { ModalMixin } from '~/assets/vue/mixins'
import { TextInput, Checkbox, SVGIcon } from '~/components/interface'
import analytics from '~/assets/util/analytics'
import SoundSelector from '~/components/events/view/breakdown/clips-sections/SoundSelector'

export default {
	name: 'ModalHighlightCreating',
	components: {
		ValidationObserver,
		TextInput,
		Checkbox,
		SVGIcon,
		SoundSelector
	},
	mixins: [ModalMixin],
	inject: {
		mainEvent: {
			default: null
		}
	},
	props: {
		options: {
			type: Object,
			required: true
		}
	},
	data: () => ({
		loading: false,
		clipData: {
			name: '',
			soundtrack: 'electronic',
			sourceAudioVolume: 1,

			// Passed from options
			targetId: '',
			url: '',
			tagIds: [],
			branding: 'pixellot'
		}
	}),
	computed: {
		isAudioSelected () {
			return Boolean(this.clipData.sourceAudioVolume)
		},
		isMusicSelected () {
			return Boolean(this.clipData.soundtrack)
		}
	},
	watch: {
		'options.payload': {
			immediate: true,
			deep: true,
			handler (payload) {
				this.clipData = Object.assign(this.clipData, payload)
			}
		}
	},
	methods: {
		...mapActions({ analyticsTrack: 'analytics/track' }),

		onAudioCheckboxChange (val) {
			this.clipData.sourceAudioVolume = val ? 1 : 0
		},
		onMusicCheckboxChange (val) {
			this.clipData.soundtrack = val ? 'electronic' : ''
		},
		onSave () {
			this.loading = true

			this.$axios.$post('/api/v1/users/me/clips/tags', this.clipData)
				.then(this.onSuccess)
				.catch(this.onError)
		},
		onSuccess (clip) {
			this.$toast.success(this.$tc('successMessages.highlightClipCreateSuccessMessage'))
			this.loading = false
			this.onConfirm(clip)

			const payload = {
				playMode: this.$route.query.playMode,
				event: this.mainEvent
			}

			this.analyticsTrack({
				event: analytics.CreateHighlight.event,
				data: analytics.CreateHighlight.getData(payload)
			})
		},
		onError (error) {
			this.$log.error('Failed to create new highlight clip', error)
			this.$toast.error(this.$tc('errors.highlightClipCreateError'))
			this.loading = false
			this.onClose()

			const payload = {
				playMode: this.$route.query.playMode,
				event: this.mainEvent,
				highlightType: 'clip'
			}

			this.analyticsTrack({
				event: analytics.CreateHighlightFailure.event,
				data: analytics.CreateHighlightFailure.getData(payload)
			})
		}
	}
}
</script>

<style lang="scss">
.clip-name {
  .text-input {
    padding-bottom: 20px;
    padding-left: 0;
    margin-top: 13px;
  }
}
</style>
